<template>
  <div class="landing-page-hero">
    <div class="landing-page-hero-text-wrapper">
      <div class="landing-page-hero-text">
        <div class="landing-page-hero-text-title">
          {{ __('landing_title') }}
        </div>
        <div class="landing-page-hero-text-box">
          <p class="body-text">{{ __('landing_subtitle') }}</p>
          <p class="body-text">
            {{ __('landing_second_subtitle') }}
          </p>
        </div>
        <router-link
          :to="{
            name: 'LandingPage',
            hash: '#contact',
          }"
          class="button">
          <img src="@/assets/icons/starIcon.svg" alt="" />
          {{ __('landing_button') }}
        </router-link>
      </div>
    </div>

    <img src="@/assets/images/ND-hero.webp" class="landing-page-hero-image" />
  </div>

  <div class="landing-page-services">
    <div class="section-title">{{ __('services_title') }}</div>
    <div class="landing-page-services-box">
      <div class="landing-page-services-box-item" v-for="service in services" :key="service.title">
        <p class="title-text">
          {{ __(service.title) }}
        </p>
        <div class="border-space-breaker"></div>
        <div class="landing-page-services-box-item-description">
          <p class="body-text" v-for="(description, index) in service.description" :key="index">
            {{ __(description) }}
          </p>
        </div>
      </div>
    </div>
    <SwiperComponent :items="services" children-class=".services-box">
      <template #item="{ item }">
        <div class="services-box">
          <p class="title-text">
            {{ __(item.title) }}
          </p>
          <div class="border-space-breaker"></div>
          <div class="services-box-description">
            <p class="body-text" v-for="(description, index) in item.description" :key="index">
              {{ __(description) }}
            </p>
          </div>
        </div>
      </template>
    </SwiperComponent>
  </div>
  <div class="grey-box">
    <div class="landing-page-about">
      <div class="landing-page-about-image">
        <img src="@/assets/images/dentist.webp" />
      </div>
      <div class="landing-page-about-info">
        <div class="section-title">{{ __('dentist_title') }}</div>
        <p class="body-text">
          {{ __('dentist_description') }}
        </p>
      </div>
    </div>
  </div>
  <div class="landing-page-products">
    <div class="section-title">{{ __('products_title') }}</div>
    <SwiperComponent :items="store.products" children-class=".product-item">
      <template #item="{ item }">
        <router-link :to="{ name: 'ProductDetails', params: { id: item.id } }" class="product-item">
          <img :src="item.imageUrl" />
          <div class="product-item-title">
            <p class="title-text">{{ item.name }}</p>
            <p class="body-text">{{ item.description }}</p>
          </div>

          <div class="border-space-breaker"></div>
          <p class="body-text-bold">
            {{ store.formatPrice(item.price) }}
          </p>
        </router-link>
      </template>
    </SwiperComponent>
  </div>

  <div class="landing-page-location">
    <div id="map" class="map" ref="mapElement"></div>
    <div class="landing-page-location-wrapper">
      <div class="landing-page-location-info">
        <div class="section-title limit-width">
          {{ __('map_location_block_title') }}
        </div>
        <div class="landing-page-location-info-selector">
          <template v-for="(l, index) in locations" :key="index">
            <input
              type="radio"
              :id="l.location"
              :value="index"
              v-model="activeLocation"
              class="landing-page-location-info-selector-input" />
            <label :for="l.location" class="landing-page-location-info-selector-label">
              {{ l.location }}
            </label>
          </template>
        </div>
        <div class="landing-page-location-info-box">
          <p class="body-text-bold">{{ __('location_title') }}</p>
          <p class="body-text">
            {{ locations[activeLocation].address }}
          </p>
          <a
            :href="locations[activeLocation].mapLink"
            rel="noopener noreferrer"
            aria-label="Open in Google Maps"
            target="_blank"
            class="landing-page-location-info-box-row">
            <p class="link-text">{{ __('location_action') }}</p>
            <div class="arrow right-arrow"></div>
          </a>
        </div>

        <div class="landing-page-location-info-box">
          <p class="body-text-bold">{{ __('working_hours') }}</p>
          <div
            class="landing-page-location-info-box-hours"
            v-for="(day, index) in locations[activeLocation].workingHours"
            :key="index">
            <p class="body-text">
              {{ __(dayMap[index]) }}
            </p>
            <p class="body-text">{{ formatWorkingHours(day.start) }} - {{ formatWorkingHours(day.end) }}</p>
          </div>
        </div>

        <div class="landing-page-location-info-box">
          <p class="body-text-bold">{{ __('contact_title') }}</p>
          <a
            :href="`tel:${locations[activeLocation].phone}`"
            rel="noopener noreferrer"
            aria-label="Call us"
            target="_blank"
            class="body-text">
            {{ locations[activeLocation].phone }}
          </a>
          <a
            :href="`mailto:${locations[activeLocation].email}`"
            rel="noopener noreferrer"
            aria-label="Send an email"
            class="body-text"
            target="_blank">
            {{ locations[activeLocation].email }}
          </a>
        </div>
      </div>
    </div>
  </div>
  <ContactForm />
</template>

<script lang="ts" setup>
import SwiperComponent from '@/components/SwiperComponent.vue';
import ContactForm from '@/components/ContactForm.vue';
import { onMounted, ref, useTemplateRef, watch } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import { generalStore } from '@/store';
import __ from '@/translate';
// import axios from 'axios';

const services = [
  {
    title: 'service_option_prevention',
    description: [
      'service_option_prevention',
      'service_option_first_examination',
      'service_option_check_up',
      'service_option_tartar_cleaning',
      'service_option_prevention_children',
    ],
  },
  {
    title: 'service_option_fillers',
    description: ['service_option_composite_fillings', 'service_option_aesthetic_composite_fillings'],
  },
  {
    title: 'service_option_rtg',
    description: [
      'service_option_opg',
      'service_option_bitewing_radiographs',
      'service_option_local_radiographs',
      'service_option_3d_cbct',
    ],
  },
  {
    title: 'service_option_prosthetics',
    description: [
      'service_option_veneers',
      'service_option_partial_crowns',
      'service_option_full_crowns',
      'service_option_bonding',
      'service_option_removable_prosthetics',
    ],
  },
  {
    title: 'service_option_teeth_whitening',
    description: ['service_option_safe_teeth_whitening'],
  },
  {
    title: 'service_option_surgery_and_implantology',
    description: ['service_option_apicotomy'],
  },
  {
    title: 'service_option_endodontic_treatment',
    description: [
      'service_option_root_canal_treatment',
      'service_option_magnification',
      'service_option_rubber_membrane',
      'service_option_machine_expansion',
      'service_option_ultrasound',
    ],
  },
];

const locations: Record<
  string,
  {
    location: string;
    address: string;
    mapLink: string;
    email: string;
    phone: string;
    workingHours: Record<number, { start: number; end: number }>;
    map: {
      center: {
        lat: number;
        lng: number;
      };
      markerPosition: google.maps.LatLngLiteral;
    };
  }
> = {
  ljubljana: {
    location: 'Ljubljana',
    address: 'Na jami 14, 1000 Ljubljana',
    mapLink: 'https://maps.app.goo.gl/TnyeLbuXJovGDGzL6',
    email: 'info@nordic-dental.si',
    phone: '01 777 41 04',
    workingHours: {
      1: {
        start: 900,
        end: 1600,
      },
      2: {
        start: 900,
        end: 1600,
      },
      3: {
        start: 900,
        end: 1600,
      },
      4: {
        start: 900,
        end: 1600,
      },
      5: {
        start: 900,
        end: 1600,
      },
    },
    map: {
      center: { lat: 46.0668316, lng: 14.4874782 },
      markerPosition: { lat: 46.0668316, lng: 14.4897159 },
    },
  },
  vodice: {
    location: 'Vodice',
    address: 'Kopitarjev trg 1, 1217 Vodice',
    mapLink: 'https://maps.app.goo.gl/u5G23RqsfHufE8YD6',
    email: 'info@nordic-dental.si',
    phone: '01 777 41 04',
    workingHours: {
      1: {
        start: 1030,
        end: 1930,
      },
      2: {
        start: 1030,
        end: 1900,
      },
      3: {
        start: 730,
        end: 1300,
      },
      4: {
        start: 730,
        end: 1230,
      },
      5: {
        start: 730,
        end: 1200,
      },
    },
    map: {
      center: { lat: 46.1871757, lng: 14.4874782 },
      markerPosition: { lat: 46.1871757, lng: 14.4910531 },
    },
  },
};
const activeLocation = ref(Object.keys(locations)[0]);

let map: google.maps.Map | null = null;
const mapElement = useTemplateRef('mapElement');
const store = generalStore();

onMounted(async () => {
  // axios.get('api/addresses');

  await Promise.all([store.fetchProducts()]);
  const loader = new Loader({
    apiKey: 'AIzaSyDiP-0e-uI9Xkup13OeE-Lo3dL_QzbCIZE',
    version: 'weekly',
  });

  if (!mapElement.value) {
    return;
  }

  const { Map } = (await loader.importLibrary('maps')) as google.maps.MapsLibrary;
  const { AdvancedMarkerElement } = (await loader.importLibrary('marker')) as google.maps.MarkerLibrary;

  map = new Map(mapElement.value, {
    center: locations[activeLocation.value].map.center,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM,
    },
    mapId: '6896da82777ebb1f',
    zoom: 17,
    disableDefaultUI: true,
    gestureHandling: 'cooperative',
    clickableIcons: false,
    fullscreenControl: false,
    fullscreenControlOptions: null,
    keyboardShortcuts: null,
    streetView: null,
    streetViewControl: false,
    streetViewControlOptions: null,
    scrollwheel: false,
    zoomControl: true,
  });

  for (const loc in locations) {
    new AdvancedMarkerElement({
      map,
      position: locations[loc].map.markerPosition,
      title: `Nordic Dental - ${locations[loc].location}`,
      content: new DOMParser().parseFromString(
        '<svg xmlns="http://www.w3.org/2000/svg" width="54" height="77" viewBox="0 0 54 77" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M27 76.334C27 76.334 53.5 47.9356 53.5 27.7784C53.5 12.6213 41.6355 0.333984 27 0.333984C12.3645 0.333984 0.5 12.6213 0.5 27.7784C0.5 47.9356 27 76.334 27 76.334Z" fill="#15BBBD"/><path d="M32.1605 32.125C27.5015 36.136 26.9491 44.6131 26.9491 44.6131L26.9251 44.3617C26.817 42.9968 26.0365 35.7529 21.8338 32.113C17.1748 28.1019 10.5586 27.0005 10.5586 27.0005C10.5586 27.0005 17.1748 25.9109 21.8338 21.8878C26.0485 18.2599 26.817 11.0041 26.9251 9.63912L26.9491 9.3877C26.9491 9.3877 27.5015 17.8648 32.1605 21.8758C36.8195 25.8869 43.4357 26.9885 43.4357 26.9885C43.4357 26.9885 36.8195 28.1139 32.1605 32.125Z" fill="white"/></svg>',
        'image/svg+xml'
      ).documentElement,
      zIndex: 20000,
    });
  }
  map.panTo(locations[activeLocation.value].map.markerPosition);
});

watch(
  activeLocation,
  (newLocation) => {
    if (!mapElement.value) {
      return;
    }

    map?.setZoom(17);
    map?.panTo(locations[newLocation].map.markerPosition);
  },
  { immediate: true }
);

function formatWorkingHours(hours: number) {
  const hoursString = hours.toString();
  const hoursLength = hoursString.length;
  return `${hoursString.length === 3 ? '0' : ''}${hoursString.slice(0, hoursLength - 2)}:${hoursString.slice(
    hoursLength - 2
  )}`;
}

const dayMap: Record<number, string> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  7: 'sunday',
};
</script>
